import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import {
  isDigestEmailFeatureEnabled,
  isFollowFeatureEnabled,
} from "@/config/runtimeConfig";

export const CONTENT_SUBSCRIPTION_TARGET_TYPES = Object.freeze([
  "user",
  "organisation",
]);
export const CONTENT_SUBSCRIPTION_CADENCES = Object.freeze([
  "daily",
  "weekly",
  "monthly",
]);
export const CONTENT_SUBSCRIPTION_DIGEST_STATUSES = Object.freeze([
  "active",
  "paused",
  "pendingVerification",
]);
export const DIGEST_PREFERENCE_MUTABLE_STATUSES = Object.freeze([
  "active",
  "paused",
]);
export const FOLLOW_DIGEST_CONSENT_VERSION = "follow-digest-v1";

const TARGET_ID_PATTERN = /^[A-Za-z0-9._:@-]{1,128}$/;
const LOCALE_PATTERN = /^[A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,8})*$/;
const SAFE_ERROR_MESSAGES = Object.freeze({
  unauthenticated: "Sign in to manage follows.",
  "permission-denied": "This follow action is not available.",
  "invalid-argument": "The follow request is invalid.",
  "failed-precondition": "Choose digest preferences before following.",
  aborted: "Digest preferences changed. Refresh and try again.",
  "not-found": "This follow target is no longer available.",
  unavailable: "The follow service is temporarily unavailable.",
  "deadline-exceeded": "The follow service did not respond in time.",
  "invalid-response": "The follow service returned an invalid response.",
  "feature-disabled": "This feature is not available.",
  unknown: "The follow request could not be completed.",
});

export class ContentSubscriptionServiceError extends Error {
  constructor(code = "unknown") {
    const safeCode = Object.prototype.hasOwnProperty.call(
      SAFE_ERROR_MESSAGES,
      code,
    )
      ? code
      : "unknown";
    super(SAFE_ERROR_MESSAGES[safeCode]);
    this.name = "ContentSubscriptionServiceError";
    this.code = safeCode;
  }
}

function fail(code) {
  throw new ContentSubscriptionServiceError(code);
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function validateTargetType(value, errorCode = "invalid-argument") {
  if (!CONTENT_SUBSCRIPTION_TARGET_TYPES.includes(value)) fail(errorCode);
  return value;
}

function validateTargetId(value, errorCode = "invalid-argument") {
  const targetId = typeof value === "string" ? value.trim() : "";
  const reservedFirestoreId =
    targetId === "." || targetId === ".." || /^__.*__$/.test(targetId);
  if (!TARGET_ID_PATTERN.test(targetId) || reservedFirestoreId) fail(errorCode);
  return targetId;
}

function validateTargetInput(value) {
  if (!isRecord(value)) fail("invalid-argument");
  return {
    targetType: validateTargetType(value.targetType),
    targetId: validateTargetId(value.targetId),
  };
}

function isValidTimeZone(value) {
  if (typeof value !== "string" || value.length > 100) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: value }).format();
    return true;
  } catch (_error) {
    return false;
  }
}

function validateCadence(value, errorCode = "invalid-argument") {
  if (!CONTENT_SUBSCRIPTION_CADENCES.includes(value)) fail(errorCode);
  return value;
}

function validateTimeZone(value, errorCode = "invalid-argument") {
  if (!isValidTimeZone(value)) fail(errorCode);
  return value;
}

function validateLocale(value, errorCode = "invalid-argument") {
  const locale = typeof value === "string" ? value.trim() : "";
  if (locale.length > 35 || !LOCALE_PATTERN.test(locale)) fail(errorCode);
  return locale;
}

function validateDigestSetup(value) {
  if (!isRecord(value)) fail("invalid-argument");
  if (typeof value.enabled !== "boolean") fail("invalid-argument");
  const cadence = validateCadence(value.cadence);
  const timezone = validateTimeZone(value.timezone);
  const locale = validateLocale(value.locale);
  if (value.consentVersion !== FOLLOW_DIGEST_CONSENT_VERSION) {
    fail("invalid-argument");
  }

  return {
    enabled: value.enabled,
    cadence,
    timezone,
    locale,
    consentVersion: FOLLOW_DIGEST_CONSENT_VERSION,
  };
}

function validateSetDigestPreferencesInput(value) {
  if (!isRecord(value)) fail("invalid-argument");
  if (!DIGEST_PREFERENCE_MUTABLE_STATUSES.includes(value.status)) {
    fail("invalid-argument");
  }

  return {
    status: value.status,
    cadence: validateCadence(value.cadence),
    timezone: validateTimeZone(value.timezone),
    locale: validateLocale(value.locale),
  };
}

function validateSetInput(value) {
  const target = validateTargetInput(value);
  if (typeof value.following !== "boolean") fail("invalid-argument");

  const input = { ...target, following: value.following };
  if (Object.prototype.hasOwnProperty.call(value, "digestSetup")) {
    input.digestSetup = validateDigestSetup(value.digestSetup);
  }
  return input;
}

function validateListInput(value = {}) {
  if (!isRecord(value)) fail("invalid-argument");

  const input = {};
  if (value.targetType !== undefined && value.targetType !== null) {
    input.targetType = validateTargetType(value.targetType);
  }

  const limit = value.limit === undefined ? 20 : value.limit;
  if (!Number.isInteger(limit) || limit < 1 || limit > 50) {
    fail("invalid-argument");
  }
  input.limit = limit;

  if (value.cursor !== undefined && value.cursor !== null) {
    if (
      typeof value.cursor !== "string" ||
      value.cursor.length < 1 ||
      value.cursor.length > 400
    ) {
      fail("invalid-argument");
    }
    input.cursor = value.cursor;
  }

  return input;
}

/**
 * Validate and narrow the callable's canonical Follow state.
 * Unknown response fields are intentionally discarded.
 */
export function validateContentSubscriptionState(value, expectedTarget = null) {
  if (!isRecord(value)) fail("invalid-response");

  const targetType = validateTargetType(value.targetType, "invalid-response");
  const targetId = validateTargetId(value.targetId, "invalid-response");
  if (
    expectedTarget &&
    (targetType !== expectedTarget.targetType ||
      targetId !== expectedTarget.targetId)
  ) {
    fail("invalid-response");
  }
  if (
    typeof value.following !== "boolean" ||
    typeof value.requiresDigestSetup !== "boolean"
  ) {
    fail("invalid-response");
  }

  const digestStatus = value.digestStatus === null ? null : value.digestStatus;
  const cadence = value.cadence === null ? null : value.cadence;
  if (value.requiresDigestSetup) {
    if (digestStatus !== null || cadence !== null) fail("invalid-response");
  } else if (
    !CONTENT_SUBSCRIPTION_DIGEST_STATUSES.includes(digestStatus) ||
    !CONTENT_SUBSCRIPTION_CADENCES.includes(cadence)
  ) {
    fail("invalid-response");
  }

  return {
    targetType,
    targetId,
    following: value.following,
    requiresDigestSetup: value.requiresDigestSetup,
    digestStatus,
    cadence,
  };
}

function validateListResponse(value) {
  if (!isRecord(value) || !Array.isArray(value.subscriptions)) {
    fail("invalid-response");
  }
  if (
    value.nextCursor !== null &&
    (typeof value.nextCursor !== "string" ||
      value.nextCursor.length < 1 ||
      value.nextCursor.length > 400)
  ) {
    fail("invalid-response");
  }

  const subscriptions = value.subscriptions.map((subscription) => {
    if (!isRecord(subscription)) fail("invalid-response");
    if (
      typeof subscription.id !== "string" ||
      subscription.id.length < 1 ||
      subscription.id.length > 400
    ) {
      fail("invalid-response");
    }

    const normalized = {
      id: subscription.id,
      targetType: validateTargetType(
        subscription.targetType,
        "invalid-response",
      ),
      targetId: validateTargetId(subscription.targetId, "invalid-response"),
    };
    if (subscription.createdAt !== undefined) {
      normalized.createdAt = subscription.createdAt;
    }
    if (subscription.updatedAt !== undefined) {
      normalized.updatedAt = subscription.updatedAt;
    }
    return normalized;
  });

  return { subscriptions, nextCursor: value.nextCursor };
}

function validateNextDueAt(value) {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string" || value.length > 40) {
    fail("invalid-response");
  }
  const parsed = new Date(value);
  if (
    Number.isNaN(parsed.getTime()) ||
    parsed.toISOString() !== value
  ) {
    fail("invalid-response");
  }
  return value;
}

/**
 * Validate the account-wide digest preference returned by the private callable.
 * A false `configured` value is the truthful state before the first Follow.
 */
export function validateDigestPreferences(value) {
  if (!isRecord(value) || typeof value.configured !== "boolean") {
    fail("invalid-response");
  }
  if (typeof value.hasVerifiedEmail !== "boolean") fail("invalid-response");

  if (!value.configured) {
    if (
      value.status !== null ||
      value.cadence !== null ||
      value.timezone !== null ||
      value.locale !== null ||
      value.nextDueAt !== null
    ) {
      fail("invalid-response");
    }
    return {
      configured: false,
      status: null,
      cadence: null,
      timezone: null,
      locale: null,
      hasVerifiedEmail: value.hasVerifiedEmail,
      nextDueAt: null,
    };
  }

  if (!CONTENT_SUBSCRIPTION_DIGEST_STATUSES.includes(value.status)) {
    fail("invalid-response");
  }

  const nextDueAt = validateNextDueAt(value.nextDueAt);
  if (
    (value.status === "active" && nextDueAt === null) ||
    (value.status !== "active" && nextDueAt !== null)
  ) {
    fail("invalid-response");
  }

  return {
    configured: true,
    status: value.status,
    cadence: validateCadence(value.cadence, "invalid-response"),
    timezone: validateTimeZone(value.timezone, "invalid-response"),
    locale: validateLocale(value.locale, "invalid-response"),
    hasVerifiedEmail: value.hasVerifiedEmail,
    nextDueAt,
  };
}

function normalizeRemoteError(error) {
  if (error instanceof ContentSubscriptionServiceError) return error;

  const rawCode = typeof error?.code === "string" ? error.code : "";
  const code = rawCode.includes("/")
    ? rawCode.slice(rawCode.lastIndexOf("/") + 1)
    : rawCode;
  return new ContentSubscriptionServiceError(code || "unknown");
}

export function createContentSubscriptionService({
  functionsInstance = functions,
  callableFactory = httpsCallable,
  followEnabled = isFollowFeatureEnabled,
  digestEmailEnabled = isDigestEmailFeatureEnabled,
} = {}) {
  const callables = new Map();
  const requireFollowFeature = () => {
    if (followEnabled !== true) fail("feature-disabled");
  };
  const requireDigestEmailFeature = () => {
    if (digestEmailEnabled !== true) fail("feature-disabled");
  };
  const getCallable = (name) => {
    if (!callables.has(name)) {
      callables.set(name, callableFactory(functionsInstance, name));
    }
    return callables.get(name);
  };

  const invoke = async (name, payload, validateResponse) => {
    try {
      const response = await getCallable(name)(payload);
      return validateResponse(response?.data);
    } catch (error) {
      throw normalizeRemoteError(error);
    }
  };

  return Object.freeze({
    async getContentSubscription(input) {
      requireFollowFeature();
      const target = validateTargetInput(input);
      return invoke("getContentSubscription", target, (response) =>
        validateContentSubscriptionState(response, target),
      );
    },

    async setContentSubscription(input) {
      requireFollowFeature();
      const request = validateSetInput(input);
      if (Object.prototype.hasOwnProperty.call(request, "digestSetup")) {
        requireDigestEmailFeature();
      }
      const target = {
        targetType: request.targetType,
        targetId: request.targetId,
      };
      return invoke("setContentSubscription", request, (response) =>
        validateContentSubscriptionState(response, target),
      );
    },

    async listContentSubscriptions(input = {}) {
      requireFollowFeature();
      const request = validateListInput(input);
      return invoke(
        "listContentSubscriptions",
        request,
        validateListResponse,
      );
    },

    async getDigestPreferences() {
      requireDigestEmailFeature();
      return invoke("getDigestPreferences", {}, validateDigestPreferences);
    },

    async setDigestPreferences(input) {
      requireDigestEmailFeature();
      const request = validateSetDigestPreferencesInput(input);
      return invoke(
        "setDigestPreferences",
        request,
        validateDigestPreferences,
      );
    },
  });
}

export const contentSubscriptionService = createContentSubscriptionService();

export const getContentSubscription = (input) =>
  contentSubscriptionService.getContentSubscription(input);
export const setContentSubscription = (input) =>
  contentSubscriptionService.setContentSubscription(input);
export const listContentSubscriptions = (input) =>
  contentSubscriptionService.listContentSubscriptions(input);
export const getDigestPreferences = () =>
  contentSubscriptionService.getDigestPreferences();
export const setDigestPreferences = (input) =>
  contentSubscriptionService.setDigestPreferences(input);
